import React from 'react';
import { createContainer } from 'unstated-next';
import { last, includes } from 'lodash';
import {
    ADD_TILES_TO_BOARD_FROM_HAND,
    ADD_TILES_TO_HAND_FROM_BOARD,
    MOVE_TILES_ON_BOARD,
    ReducerActionTypes,
    State,
    reducer,
} from '../helpers/gameReducer';

import { setStateToStorage } from '../helpers/storage';
import { updateHandSize } from '../helpers/gameServer';

const updateHandSizeMiddleware = ({ roomId, userId }: { roomId: string, userId: string }) => (
    state: State,
    action: ReducerActionTypes,
) => {
    if (includes([ADD_TILES_TO_BOARD_FROM_HAND, ADD_TILES_TO_HAND_FROM_BOARD], action.type)) {
        const handSize = state.hand.length;
        updateHandSize({ roomId, userId, handSize });
    }
};

const localStorageMiddleware = (roomId: string) => (
    newState: State,
    action: ReducerActionTypes,
) => {
    if (
        // Note: These commands must not overlap with
        // those during peel/split/dump,
        // else they will override the localstorage
        includes(
            [
                ADD_TILES_TO_BOARD_FROM_HAND,
                ADD_TILES_TO_HAND_FROM_BOARD,
                MOVE_TILES_ON_BOARD,
                ADD_TILES_TO_HAND_FROM_BOARD,
            ],
            action.type,
        )
    ) {
        // Track what the last command was when
        // we last interacted with our board
        const lastCommandRun = last(newState.commandsRun);
        if (lastCommandRun) {
            setStateToStorage({
                [roomId]: {
                    id: lastCommandRun,
                    state: {
                        hand: newState.hand,
                        board: newState.board,
                    },
                },
            });
        }
    }
};

const reducerWithMiddlewareFactory = (
    middlewares: ((state: State, action: ReducerActionTypes) => void)[],
) => (state: State, action: ReducerActionTypes) => {
    const newState = reducer(state, action);

    middlewares.forEach((middleware) => {
        middleware(newState, action);
    });

    return newState;
};

const reducerInitialState = { hand: [], board: [], commandsRun: [] };

const useBoardState = (
    initialState:
        | {
              userId: string,
              roomId: string,
          }
        | undefined,
) => {
    const roomId = initialState?.roomId || '';
    const userId = initialState?.userId || '';
    // Store the room id into localstorage too
    const reducer = React.useMemo(() => {
        return reducerWithMiddlewareFactory([
            localStorageMiddleware(roomId),
            updateHandSizeMiddleware({ roomId, userId }),
        ]);
    }, [roomId, userId]);

    const [state, dispatch] = React.useReducer(reducer, reducerInitialState);
    return { state, dispatch };
};

export const BoardState = createContainer(useBoardState);

